import LegalLayout from './LegalLayout';

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      updated="May 2026"
      testId="privacy-page"
      notice={
        <>
          Casa de Candy values privacy, discretion, and responsible communication. This Privacy Policy
          explains how information is collected, used, protected, retained, and managed when users visit
          our website, submit information, communicate with us, or participate in appointment-related
          communications.
        </>
      }
    >
      <div className="legal-section">
        <h2>1. Scope of This Policy</h2>
        <p>
          This Privacy Policy applies to Casa de Candy websites, online forms, SMS communications,
          WhatsApp communications, phone calls, appointment inquiries, customer-support communications,
          and related business interactions.
        </p>
        <p>
          By accessing the website, submitting information, checking a consent box, calling, texting,
          messaging through WhatsApp, or continuing communication with Casa de Candy, you acknowledge
          this Privacy Policy.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. Information We Collect</h2>
        <p>Casa de Candy may collect information voluntarily provided by users, including:</p>
        <ul>
          <li>Name, preferred name, or alias if provided;</li>
          <li>Phone number;</li>
          <li>Email address if provided;</li>
          <li>SMS, WhatsApp, phone, or form communication details;</li>
          <li>Appointment inquiry details, scheduling preferences, arrival timing, and confirmation information;</li>
          <li>General location or proximity information voluntarily provided for appointment coordination;</li>
          <li>Payment confirmation details, if relevant to a transaction or appointment record;</li>
          <li>Device, browser, website interaction, or analytics information collected through standard website tools.</li>
        </ul>
        <p>
          Casa de Candy does not require users to provide unnecessary personal information. Users should
          not submit sensitive personal information unless specifically necessary for communication,
          safety, legal, or operational reasons.
        </p>
      </div>

      <div className="legal-section">
        <h2>3. How Information Is Used</h2>
        <p>Information may be used for lawful business purposes, including:</p>
        <ul>
          <li>Responding to inquiries;</li>
          <li>Coordinating appointment availability;</li>
          <li>Confirming appointment details;</li>
          <li>Providing customer support;</li>
          <li>Sending account-related or appointment-related notifications;</li>
          <li>Sending limited promotional updates where consent has been provided;</li>
          <li>Improving website performance and customer experience;</li>
          <li>Maintaining internal business records;</li>
          <li>
            Protecting the safety, privacy, rights, and property of Casa de Candy, its personnel,
            contractors, customers, and visitors;
          </li>
          <li>
            Complying with applicable legal, regulatory, accounting, security, and dispute-resolution
            obligations.
          </li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>4. SMS, WhatsApp, and Phone Communications</h2>
        <p>
          By submitting a phone number, checking a consent box, initiating communication, or continuing
          communication with Casa de Candy, you consent to receive communications related to your
          inquiry, appointment coordination, customer support, account notifications, and limited
          promotional updates.
        </p>
        <ul>
          <li>Message frequency may vary.</li>
          <li>Msg &amp; data rates may apply.</li>
          <li>Reply STOP to opt out.</li>
          <li>Reply HELP for assistance.</li>
        </ul>
        <p>
          Opt-out requests are honored in accordance with applicable law and carrier requirements. Casa
          de Candy may treat words or phrases that reasonably express a desire to stop receiving
          messages as an opt-out request.
        </p>
      </div>

      <div className="legal-section">
        <h2>5. Consent and Opt-In</h2>
        <p>
          Users may opt in by submitting a phone number through a website form, manually checking a
          consent box where provided, sending an SMS or WhatsApp message, calling and requesting
          information, or otherwise voluntarily initiating communication.
        </p>
        <p>
          Where a double opt-in confirmation is used, users may receive a confirmation message such as:
          “Casa De Candy: You have subscribed to our alerts. Message frequency may vary. Msg &amp; data
          rates may apply. Reply HELP for help, Reply STOP to cancel.”
        </p>
      </div>

      <div className="legal-section">
        <h2>6. No Sale of Personal Information</h2>
        <p>
          Casa de Candy does not sell, rent, trade, or broker personal information. Casa de Candy does
          not share personal information with third parties for their independent marketing purposes.
        </p>
      </div>

      <div className="legal-section">
        <h2>7. Service Providers and Limited Sharing</h2>
        <p>
          Casa de Candy may use service providers, technology platforms, messaging providers, hosting
          providers, payment processors, analytics tools, security tools, or professional advisors to
          support lawful business operations. These parties may access information only as reasonably
          necessary to perform their functions or comply with legal obligations.
        </p>
      </div>

      <div className="legal-section">
        <h2>8. Privacy Rights</h2>
        <p>
          Depending on applicable law and your location, you may have rights to request access,
          correction, deletion, portability, or limitation of certain personal information. You may also
          have the right to opt out of certain processing activities where applicable.
        </p>
        <p>
          Casa de Candy will evaluate privacy requests in accordance with applicable federal and state
          law. To make a privacy request, contact support@casadecandy.com.
        </p>
      </div>

      <div className="legal-section">
        <h2>9. Confidentiality and Discretion</h2>
        <p>
          Casa de Candy treats user identities, communications, scheduling details, appointment-related
          details, access instructions, location details, and operational information as private
          business information. Users are also expected to respect the privacy and confidentiality of
          Casa de Candy, its personnel, contractors, hosts, visitors, locations, and operations.
        </p>
      </div>

      <div className="legal-section">
        <h2>10. Data Security</h2>
        <p>
          Casa de Candy uses reasonable administrative, technical, and operational safeguards designed
          to protect information from unauthorized access, misuse, loss, alteration, or disclosure. No
          electronic system, website, messaging service, or internet-based communication method is
          completely secure. Users acknowledge that SMS, WhatsApp, phone, and online communications may
          carry inherent security risks.
        </p>
      </div>

      <div className="legal-section">
        <h2>11. Data Retention</h2>
        <p>
          Information may be retained as long as reasonably necessary for communication, appointment
          coordination, business records, tax or accounting records, safety, security, legal compliance,
          dispute resolution, fraud prevention, and enforcement of policies or agreements. Information
          may be deleted, archived, anonymized, or retained as legally permitted or required.
        </p>
      </div>

      <div className="legal-section">
        <h2>12. Legal Disclosure</h2>
        <p>
          Casa de Candy may disclose information if required by law, subpoena, court order, legal
          process, governmental request, safety concern, fraud prevention need, policy enforcement, or
          to protect the rights, privacy, safety, and property of Casa de Candy, its personnel,
          contractors, customers, visitors, or others.
        </p>
      </div>

      <div className="legal-section">
        <h2>13. Age Restriction</h2>
        <p>
          Casa de Candy is intended only for adults who are at least 18 years of age. Casa de Candy does
          not knowingly collect information from minors. If information from a minor is discovered, it
          may be deleted or restricted.
        </p>
      </div>

      <div className="legal-section">
        <h2>14. Changes to This Policy</h2>
        <p>
          Casa de Candy may update this Privacy Policy at any time. The updated version will be posted
          on this page with a revised “Last Updated” date. Continued use of the website or continued
          communication after an update constitutes acknowledgment of the updated policy.
        </p>
      </div>

      <div className="legal-section">
        <h2>15. Contact</h2>
        <p>
          For privacy questions, data requests, or communication support, contact:
          support@casadecandy.com.
        </p>
      </div>
    </LegalLayout>
  );
}
